import TPP_SNAP from 'fs-tpp-api/snap';
import type { Dispatch } from 'react';
import type { AnyAction, Store } from 'redux';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';

import { updatePage, deleteSection, PageAction, doMoveSection } from '../Page/actions';
import { updateTranslation } from '../Translation/actions';
// edit types
import {
  TPP_TYPE_IMAGE,
  TPP_TYPE_PAGE,
  TPP_TYPE_TRANSLATION,
  TPP_TYPE_TECHNICAL,
  TPP_TYPE_VIDEOCHANNEL,
  TPP_TYPE_DOWNLOAD,
  TPP_TYPE_DOWNLOADCENTER,
  TPP_TYPE_TILES,
  TPP_TYPE_SLIDE,
  TPP_TYPE_META_NAVIGATION,
  TPP_TYPE_SHORTURL,
  TPP_TYPE_EVENTS,
  TPP_TYPE_TRANSLATION_STATICPAGE,
} from './tppConstants';
import currentLanguageSelector from 'utils/selectors/languageSelectors';
import { removeEditableDownload } from 'features/download-center/actions';
import { addChannel } from '../VideoGallery/actions';
import { currentPageSelector, currentPageLanguageSelector } from 'utils/selectors/pageSelectors';
import { Log } from 'services/log';
import { copyToClipboard } from 'utils/clipboard';
import {
  checkDispatchDownloads,
  checkDispatchShortUrls,
  checkDispatchTranslations,
  handleUpdateByType,
  parseContentOnChange,
} from './tpp-content-change';
import { defineButtons } from './tpp-buttons';
import { globalsByTypeSelector } from 'utils/selectors/globalsSelectors';

class SnapApi {
  router: AppRouterInstance;
  _store: Store<AppState>;
  _pathname: string;
  dispatch: Dispatch<PageAction>;
  debugEnabled: boolean;
  dispatchPageAction: Dispatch<PageAction>;

  constructor(
    router: AppRouterInstance,
    store: Store<AppState>,
    pathname: string,
    dispatch: any,
    dispatchPageAction: Dispatch<PageAction>,
  ) {
    window.TPP_SNAP = TPP_SNAP;
    this.router = router;
    this.store = store;
    this._pathname = pathname;
    this.dispatch = dispatch;
    this.dispatchPageAction = dispatchPageAction;
    if (window) {
      // enable debug when local environment
      this.debugEnabled =
        window.location.hostname === 'localhost' ||
        window.location.hostname === 'local.web.geberit.com';
      this.init();
    }
  }

  set store(store: Store<AppState, AnyAction>) {
    this._store = store;
    this.dispatch = store.dispatch;
  }
  get store() {
    return this._store;
  }

  set pathname(pathname: string) {
    this._pathname = pathname;
    defineButtons(this.router, this.store, this._pathname);
  }

  get pathname() {
    return this._pathname;
  }

  logScope(scope) {
    let params = ['font-weight: bold', ''];
    Log.debug(
      '%cscope = %c' +
        JSON.stringify(
          scope,
          (key, val) => {
            if (val instanceof HTMLElement || ['permissions', 'workflows'].includes(key)) {
              params.push(val);
              return '%o';
            }
            return val;
          },
          2,
        ),
      ...params,
    );
  }

  async logRenderElement(scope) {
    if (scope.status.custom === null) {
      Log.debug(
        '%cawait TPP_SNAP.renderElement(scope.previewId) = %c%s',
        'font-weight: bold',
        '',
        JSON.stringify(await window.TPP_SNAP.renderElement(scope.previewId), null, 2),
      );

      try {
        const parsedPreviewId = JSON.parse(atob(scope.previewId));
        Object.keys(parsedPreviewId).forEach((key) => {
          try {
            parsedPreviewId[key] = JSON.parse(atob(parsedPreviewId[key]));
          } catch (ignore) {}
        });
        Log.debug(
          '%cPreviewId: %c%s',
          'font-weight: bold',
          '',
          JSON.stringify(parsedPreviewId, null, 2),
        );
      } catch (ignore) {}
    }
  }

  /**
   * Initialize TPP SNAP API
   * and enable debug button if local environment
   */
  init() {
    const self = this;

    if (this.debugEnabled && typeof window.TPP_SNAP.onInit === 'function') {
      window.TPP_SNAP.onInit(async (success) =>
        Log.info(`>>> FirstSpirit Preview is ${success ? 'now' : 'NOT'} available!`),
      );
      window.TPP_SNAP.registerButton({
        css: 'tpp-icon-debug',
        isEnabled: () => true,
        isVisible: () => true,
        execute: (scope) => {
          (async () => {
            scope.previewId = scope.$node.dataset.previewId as string;

            console.group('Custom Button Scope');
            self.logScope(scope);
            await self.logRenderElement(scope);
            console.groupEnd();
          })();
        },
      });
      const lastButton = window.TPP_SNAP._buttons.pop();
      if (lastButton) {
        window.TPP_SNAP._buttons.unshift(lastButton);
      }
    }

    this.configureToolbar();
    this.setupListeners();
    this.registerButtons();
    this.registerImageCropFormat();
    this.toggleCssEditState();
  }

  /**
   * Configure CC toolbar favorites
   */
  configureToolbar() {
    if (typeof window !== 'undefined' && window['CtxHost'] && window['CxtHost'].client) {
      const shadowRoot = window['CxtHost'].client.shadowRoot;
      if (shadowRoot) {
        try {
          const element = shadowRoot.querySelector('.fs-toolbar-actions');
          element.style.display = '';
          if (element.children && element.children.length > 0) {
            element.children[0].style.display = 'none';
          }
        } catch (e) {}
      }
    }
  }

  /**
   * Setup callback listeners
   */
  setupListeners() {
    // preview element from cms navigation
    window.TPP_SNAP.onRequestPreviewElement(async (previewId, ...args) => {
      const nextPage = await window.TPP_SNAP.renderElement(previewId);
      if (!nextPage.url) {
        const currentPage = currentPageSelector(this.store.getState());
        if (nextPage.videos && currentPage.type?.includes('videocenter')) {
          this.dispatch(addChannel({ ...nextPage, url: currentPage.url }));
        }
        return;
      }
      const pathname = this.pathname.split('?')[0];
      if (nextPage.url !== pathname) {
        // maybe the next page is a new page. to handle this case
        // we submit the preview id and the current language as hidden url to
        // the nextjs server. the server will know that if the content service
        // hasn't data for the given url, the site is available directly via tpp
        // and we will load it later.
        this.router.push(
          // url submitted to the server
          `${nextPage.url}${
            nextPage.url.endsWith('/') || nextPage.url.endsWith('.html') ? '' : '/'
          }?preview_id=${previewId}&page_type=${nextPage.type}&currentLanguage=${nextPage.currentLanguage}`,
        );
      } else {
        // same page than before
        // needed to update the cc context (workflow) e.g. when page is renamed
        TPP_SNAP.setPreviewElement(previewId);
      }
    });

    // rerender page with whole page object
    window.TPP_SNAP.onRerenderView(async (...args) => {
      const newPage = await window.TPP_SNAP.renderElement(
        await window.TPP_SNAP.getPreviewElement(),
      );
      this.dispatchPageAction(
        updatePage({
          previewId: newPage.previewId,
          content: newPage,
        }),
      );
    });

    // navigation changed callback
    window.TPP_SNAP.onNavigationChange(async (previewId) => {
      const isDeleted =
        !previewId &&
        !(await TPP_SNAP.getElementStatus(await TPP_SNAP.getPreviewElement())).elementType;
      if (isDeleted) {
        const newPage = await window.TPP_SNAP.renderElement(previewId);
        this.dispatchPageAction(
          updatePage({
            previewId: newPage.previewId,
            content: newPage,
          }),
        );
      }
    });

    // content changed callback
    window.TPP_SNAP.onContentChange(($node, previewId, content) => {
      content = parseContentOnChange(this.store.getState(), this.dispatch, content, previewId);

      checkDispatchShortUrls(this._pathname, content, $node, previewId);
      checkDispatchDownloads(this._pathname, content, $node, previewId);
      checkDispatchTranslations(this._pathname, content, this.store.getState(), this.dispatch);

      if ($node?.dataset.editType === TPP_TYPE_EVENTS) {
        window.dispatchEvent(new CustomEvent('fscc.update.events'));
        return;
      }

      if (content === null) {
        if ($node === null || $node?.dataset?.editType === TPP_TYPE_PAGE) {
          this.router.push('/', '/');
          return;
        } else if ($node?.dataset?.editType === TPP_TYPE_DOWNLOAD) {
          this.dispatch(removeEditableDownload(previewId));
        } else {
          this.dispatchPageAction(deleteSection(previewId));
          return;
        }
      }

      // on a empty page we dont have access to $node
      if ($node?.dataset?.tppIsPage || (!$node && content !== null)) {
        const state = this.store.getState();
        const page = currentPageSelector(state);

        this.dispatchPageAction(
          updatePage({
            previewId: page.previewId,
            content: page,
          }),
        );
        return;
      }

      if ($node?.dataset) {
        handleUpdateByType(
          this.dispatch,
          this.dispatchPageAction,
          $node,
          previewId,
          content,
          this.store.getState(),
        );
      }

      return null;
    });
  }

  /**
   * Register Snap Buttons
   */
  registerButtons() {
    this.registerSectionReferenceAddButton();
    this.registerEmptyAreaButton();
    this.registerCreateNewSection();
    this.registerDuplicateButton();
    this.registerCatalogEditButton();
    this.registerVideoChannelAddButton();
    this.registerVideoChannelAddEmptyButton();
    this.registerDownloadAddButton();
    this.registerShorturlAddButton();
    this.registerMoveButton();
    this.registerTranslationEditButton();
    this.disableSnapMoveButton();
    this.disableDeleteButton();
    defineButtons(this.router, this.store, this._pathname);
    this.registerCopyURLButton();
    this.registerEventsAddButton();
  }

  /**
   * Page level button to create new section
   * Has to be overridden to keep the scroll position
   */
  registerCreateNewSection() {
    window.TPP_SNAP.overrideDefaultButton('add-sibling-section', {
      execute: ({ previewId }) => {
        window.TPP_SNAP.createSection(previewId, { result: true }).then((item) => {
          if (!item) {
            return;
          }

          const originalSection = document.querySelector(`[data-preview-id="${previewId}"]`);
          if (originalSection) {
            const originalSectionDimensions = originalSection.getBoundingClientRect();
            const newSectionTop =
              window.scrollY + originalSectionDimensions.y + originalSectionDimensions.height - 100;
            window.sessionStorage.setItem(
              'cc_scroll',
              String(newSectionTop < 0 ? 0 : newSectionTop),
            );
          }
          window.TPP_SNAP.triggerRerenderView();
        });
      },
    });
  }

  /**
   * Empty content area button to show
   * add button
   */
  registerEmptyAreaButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Absatz hinzufügen' : 'Add Section',
      isVisible: ({ $node, status }) =>
        $node.dataset.editType !== TPP_TYPE_VIDEOCHANNEL &&
        status.custom !== null &&
        status.parts &&
        status.parts[0] === 'newSection',
      isEnabled: () => true,
      execute: ({ status }) => {
        window.TPP_SNAP.createSection(status.parts[1], { body: status.parts[2] });
      },
    });
  }

  registerSectionReferenceAddButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Absatz hinzufügen' : 'Add Section',
      isVisible: ({ status }) => status.elementType === 'SectionReference',
      isEnabled: () => true,
      execute: () => {
        const rootPreviewId = document.body?.getAttribute('data-preview-id');
        if (rootPreviewId) {
          window.TPP_SNAP.createSection(rootPreviewId, { body: 'content' });
        }
      },
    });
  }

  /**
   * Duplicate elementtype
   */
  registerDuplicateButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-bookmark-create',
      isVisible: ({ $node, status }) =>
        Boolean(
          ['Section', 'Dataset'].includes(status.elementType) &&
            $node &&
            $node.dataset &&
            $node.dataset.editType &&
            $node.dataset.editType !== TPP_TYPE_SHORTURL &&
            $node.dataset.editType !== TPP_TYPE_EVENTS &&
            $node.dataset.editType !== TPP_TYPE_TRANSLATION_STATICPAGE,
        ),
      isEnabled: () => true,
      getIcon: ({ $button, status }) =>
        $button.classList.add(
          status.bookmark ? 'tpp-icon-bookmark-delete' : 'tpp-icon-bookmark-create',
        ),
      getLabel: ({ language }) => (language?.toLowerCase() === 'de' ? 'Kopiervorlage' : 'Bookmark'),
      execute: ({ previewId }) => {
        window.TPP_SNAP.toggleBookmark(previewId);
      },
    });
  }

  /**
   * Download Add Button
   */
  registerDownloadAddButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Download hinzufügen' : 'Add download',
      isVisible: ({ $node, status }) =>
        $node.dataset.editType === TPP_TYPE_DOWNLOAD ||
        (status.custom !== null && status.parts && status.parts[0] === 'newDownload'),
      isEnabled: () => true,
      execute: (sender) => {
        TPP_SNAP.createDataset('local.downloads', { result: true }).then((newItem) => {
          if (newItem) {
            window.dispatchEvent(
              new CustomEvent('fscc.update.downloads', {
                detail: {
                  type: 'add',
                  sender,
                  newItem,
                },
              }),
            );
          }
        });
      },
    });
  }

  /**
   * Video Channel Add Button
   */
  registerVideoChannelAddButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Video Kanal hinzufügen' : 'Add video channel',
      isVisible: ({ status }) => status.elementType === 'Dataset[videos]',
      isEnabled: () => true,
      execute: () => {
        window.TPP_SNAP.execute('class:NewContent', { content2: 'videos' });
      },
    });
  }

  /**
   * Video Channel Add Button
   */
  registerVideoChannelAddEmptyButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Video Kanal hinzufügen' : 'Add video channel',
      isVisible: ({ $node, status }) =>
        $node.dataset.editType === TPP_TYPE_VIDEOCHANNEL &&
        status.custom !== null &&
        status.parts &&
        status.parts[0] === 'newSection',
      isEnabled: () => true,
      execute: () => {
        window.TPP_SNAP.execute('class:NewContent', { content2: 'videos' });
      },
    });
  }

  /**
   * FS Catalog edit button
   */
  registerCatalogEditButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-edit',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Element bearbeiten' : 'Edit element',
      isVisible: ({ status }) =>
        status.custom !== null &&
        status.parts &&
        ['catalogElement', 'shorturls'].includes(status.parts[0]) &&
        status.parts[4] !== 'true',
      isEnabled: () => true,
      execute: ({ status }) => {
        window.TPP_SNAP.execute(
          'class:com.espirit.ps.geberit.module.tpp.enhancements.EditCardExecutable',
          {
            card_provider: status.parts[1],
            card_index: `${status.parts[3]}`,
            preview_id: status.parts[2],
          },
        ).then((e) => {
          window.TPP_SNAP.triggerChange(status.parts[2], e);
        });
      },
    });
  }

  /**
   * Copy "URL with anchor"-button
   */
  registerCopyURLButton() {
    const items = [
      { label: 'Copy Preview URL', value: 'preview' as const },
      { label: 'Copy Live URL', value: 'live' as const },
    ];

    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-link',
      getItems: () => items,
      isEnabled: () => true,
      isVisible: (scope) => {
        return (
          (scope.status.elementType === 'Section' ||
            scope.status.elementType === 'SectionReference') &&
          this.findAnchor(scope.$node)
        );
      },
      execute: (scope, item: (typeof items)[number]) => {
        if (!item) return;
        const globalProjectData = globalsByTypeSelector(
          this.store.getState(),
          'global_project_data',
        );
        let anchorId = this.findAnchor(scope.$node);

        let href, pathname;
        try {
          const url = new URL(scope.$node.baseURI);
          url.searchParams.delete('preview_id');
          url.searchParams.delete('currentLanguage');
          url.searchParams.delete('page_type');
          if (url.searchParams.size === 0) {
            url.search = '';
          }
          pathname = url.pathname;
          href = url.href;
        } catch {}

        const anchorUrl =
          item.value === 'preview'
            ? `${href}#${anchorId ?? ''}`
            : `${(globalProjectData as LanguageSpecificGlobals)?.projectHost}${pathname}#${
                anchorId ?? ''
              }`;

        copyToClipboard(anchorUrl);
      },
    });
  }

  findAnchor(node) {
    const childNodes = node.childNodes;

    for (let item of childNodes) {
      if (item.id?.startsWith('section-')) {
        return item.id;
      }
    }
    for (let item of childNodes) {
      const anchorId = this.findAnchor(item);
      if (anchorId) {
        return anchorId;
      }
    }

    return null;
  }

  /**
   * Moving sections up or down
   */
  checkMoveButtonVisibility({ $node, status }) {
    if ($node?.dataset?.editType === TPP_TYPE_IMAGE) return false;
    if (status?.parts?.[0] === 'newSection') return false;

    return ![
      TPP_TYPE_IMAGE,
      TPP_TYPE_TECHNICAL,
      TPP_TYPE_SHORTURL,
      TPP_TYPE_EVENTS,
      TPP_TYPE_DOWNLOAD,
      TPP_TYPE_DOWNLOADCENTER,
      TPP_TYPE_VIDEOCHANNEL,
      TPP_TYPE_PAGE,
      TPP_TYPE_SLIDE,
      TPP_TYPE_TILES,
      TPP_TYPE_TRANSLATION,
      TPP_TYPE_META_NAVIGATION,
      TPP_TYPE_TRANSLATION_STATICPAGE,
    ].includes($node.dataset.editType);
  }

  registerMoveButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-up',
      getLabel: ({ language }) => (language?.toLowerCase() === 'de' ? 'Hoch' : 'Up'),
      isEnabled: (event) => this.checkMoveButtonVisibility(event),
      isVisible: (event) => this.checkMoveButtonVisibility(event),
      execute: ({ previewId }) => {
        this.dispatchPageAction(doMoveSection(previewId, 'UP'));
        window.TPP_SNAP.execute(
          'class:com.espirit.ps.geberit.module.tpp.enhancements.SortSectionsExecutable',
          {
            previewId,
            mode: 'up',
          },
        );
        document.querySelector(`[data-preview-id="${previewId}"]`)?.scrollIntoView();
      },
    });
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-down',
      getLabel: ({ language }) => (language?.toLowerCase() === 'de' ? 'Runter' : 'Down'),

      isEnabled: (event) => this.checkMoveButtonVisibility(event),
      isVisible: (event) => this.checkMoveButtonVisibility(event),
      execute: ({ previewId }) => {
        (async () => {
          this.dispatchPageAction(doMoveSection(previewId, 'DOWN'));
          await window.TPP_SNAP.execute(
            'class:com.espirit.ps.geberit.module.tpp.enhancements.SortSectionsExecutable',
            {
              previewId,
              mode: 'down',
            },
          );
          document.querySelector(`[data-preview-id="${previewId}"]`)?.scrollIntoView();
        })();
      },
    });
  }

  /**
   * Translation edit button
   */
  registerTranslationEditButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-edit',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Übersetzung bearbeiten' : 'Edit translation',
      isVisible: ({ status }) =>
        status.custom !== null && status.parts && status.parts[0] === 'translationKey',
      isEnabled: () => true,
      execute: ({ status }) => {
        window.TPP_SNAP.execute(
          'class:com.espirit.ps.geberit.module.tpp.enhancements.EditTranslationExecutable',
          {
            key: status.parts[2],
            language: status.parts[1],
          },
        ).then((e) => {
          const currentLanguage = currentLanguageSelector(this.store.getState());
          if (currentLanguage) {
            this.dispatch(updateTranslation(e, currentLanguage));
          }
        });
      },
    });
  }

  /**
   * shorturl edit button
   */
  registerShorturlAddButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Neue ShortURL' : 'New ShortURL',
      isVisible: ({ status }) =>
        status.elementType === 'Dataset[short_urls]' ||
        (status.custom !== null && status.parts && status.parts[0] === 'newShortUrl'),
      isEnabled: () => true,
      execute: (sender) => {
        TPP_SNAP.createDataset('local.short_urls', { result: true }).then((newItem) => {
          if (newItem) {
            window.dispatchEvent(
              new CustomEvent('fscc.update.shorturls', {
                detail: {
                  type: 'add',
                  sender,
                  newItem,
                },
              }),
            );
          }
        });
      },
    });
  }

  /**
   * shorturl delete button
   */
  registerShorturlDeleteButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-delete',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Short URL Löschen' : 'Remove Short URL',
      isVisible: ({ status }) =>
        status.elementType === 'Dataset[short_urls]' &&
        status.workflows &&
        !status.workflows.current,
      isEnabled: () => true,
      execute: ({ previewId }) => {
        window.TPP_SNAP.startWorkflow(previewId, 'delete');
      },
    });
  }

  /**
   * events edit button
   */
  registerEventsAddButton() {
    window.TPP_SNAP.registerButton({
      css: 'tpp-icon-add-section',
      getLabel: ({ language }) =>
        language?.toLowerCase() === 'de' ? 'Event hinzufügen' : 'Add event',
      isVisible: ({ $node, status }) =>
        $node.dataset.editType === TPP_TYPE_EVENTS ||
        (status.custom !== null && status.parts && status.parts[0] === 'newEvent'),
      isEnabled: () => true,
      execute: () => {
        TPP_SNAP.createDataset('local.events', { result: true }).then((result) => {
          if (result) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('fscc.update.events'));
            }, 2000);
          }
        });
      },
    });
  }

  /**
   * Setup image crop format via custom data attribute
   */
  registerImageCropFormat() {
    window.TPP_SNAP.overrideDefaultButton('crop', {
      execute: ({ $node, previewId }) => {
        let formats = [$node.dataset.imageFormat];

        if (
          $node.dataset.imageFormat !== $node.dataset.imageFormat2x &&
          $node.dataset.imageFormat2x
        ) {
          formats = [...formats, $node.dataset.imageFormat2x];
        }

        if (
          $node.dataset.imageFormat !== $node.dataset.imageFormat3x &&
          $node.dataset.imageFormat2x !== $node.dataset.imageFormat3x &&
          $node.dataset.imageFormat3x
        ) {
          formats = [...formats, $node.dataset.imageFormat3x];
        }

        window.TPP_SNAP.cropImage(previewId, formats);
      },
    });
  }

  /**
   * Remove snap drag and drop move button
   */
  disableSnapMoveButton() {
    window.TPP_SNAP.overrideDefaultButton('move', {
      isVisible: () => false,
      isEnabled: () => false,
    });
  }

  /**
   * Remove delete button for master content and translations
   */
  disableDeleteButton() {
    window.TPP_SNAP.overrideDefaultButton('delete', {
      isVisible: ({ status, $node }) => {
        const isMetaNavigation = status.name === 'meta_navigation_1';
        const isFooter = status.name === 'footer';
        const isHeaderPin = status.name === 'header_pin_list';

        if (
          isFooter ||
          isMetaNavigation ||
          isHeaderPin ||
          status.elementType === 'Dataset[translations]' ||
          status?.parts?.[0] === 'translationKey' ||
          (status?.parts?.[0] === 'newShortUrl' && status?.parts?.[1] === 'ContentAreaPlaceholder')
        ) {
          return false;
        }

        const state = this.store.getState();
        const currentPageLanguage = currentPageLanguageSelector(state);
        const isTechnicalPage = !currentPageLanguage;
        if (isTechnicalPage) {
          return status.permissions.delete;
        }

        const isMasterContent = $node.dataset.isInSubscription !== 'true';
        return isMasterContent;
      },
      isEnabled: ({ $node }) => {
        return $node.dataset.isInSubscription !== 'true';
      },
    });
  }

  /**
   * add css class during tpp execution (loaders)
   */
  toggleCssEditState() {
    window.TPP_SNAP._buttons.forEach((button) => {
      button.beforeExecute = ({ $node }) => {
        $node.classList.add('tpp-disabled-node');
      };
      button.afterExecute = ({ $node }) => {
        setTimeout(() => $node.classList.remove('tpp-disabled-node'), 600);
      };
    });
  }
}

type SnapApiInterface = InstanceType<typeof SnapApi>;
export type { SnapApiInterface };
export default SnapApi;
